import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { MuiMaterials } from "../../@index.js";
import { BRAND_GOLD } from "./brandGold.js";
import { getWindowChromeThemeColor } from "./windowChromeTheme.js";

/** True on phones and tablets (≤900px). Used to compact site tool headers. */
const COMPACT_CHROME_MAX_WIDTH_PX = 900;

export function useNarrowChrome() {
    const [narrow, setNarrow] = useState(
        () => typeof window !== "undefined"
            && window.matchMedia(`(max-width: ${COMPACT_CHROME_MAX_WIDTH_PX}px)`).matches,
    );
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${COMPACT_CHROME_MAX_WIDTH_PX}px)`);
        const onChange = () => setNarrow(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return narrow;
}

export const CHROME_ACTIONS_FILL_CLASS = "chrome-actions-fill";

const ChromeHeaderLayoutContext = createContext({ placeInBar: false });

/** Whether ChromeHeaderBar moved `actions` to the strip below the title row. */
export function useChromeHeaderLayout() {
    return useContext(ChromeHeaderLayoutContext);
}

const ACTION_SCROLL_CHILD_SX = {
    "& > *": {
        flex: "0 0 auto !important",
        width: "auto !important",
        maxWidth: "none",
        minWidth: 0,
    },
    [`& > .${CHROME_ACTIONS_FILL_CLASS}`]: {
        flex: "1 1 100% !important",
        width: "100% !important",
        maxWidth: "100% !important",
        minWidth: "0 !important",
        alignSelf: "stretch",
    },
    [`& > .${CHROME_ACTIONS_FILL_CLASS} .MuiFormControl-root`]: {
        width: "auto !important",
        maxWidth: "none !important",
        flex: "0 0 auto",
    },
    "& .MuiFormControl-root": {
        width: "auto !important",
        minWidth: 140,
        maxWidth: 200,
        flex: "0 0 auto !important",
    },
    "& > [data-chrome-spacer]": {
        display: "block",
        flex: { xs: "0 0 0 !important", sm: "1 1 12px !important" },
        minWidth: { xs: "0 !important", sm: 12 },
        maxWidth: { xs: "0 !important", sm: "none" },
        width: { xs: "0 !important", sm: "auto" },
        height: 0,
        overflow: "hidden",
        pointerEvents: "none",
        p: 0,
        m: 0,
        border: 0,
    },
};

const ACTION_BAR_SCROLL_SX = {
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-x",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
        display: "none",
        height: 0,
        width: 0,
    },
};

/**
 * Toolbar strip under the window title row (same chrome as Cable Health stats).
 * When children do not fit, the strip scrolls horizontally instead of wrapping or shrinking.
 */
export function ChromeActionBar({
    children,
    paper,
    border,
    /** Kept for callers; strip always scrolls rather than wrapping into a grid. */
    grid = true,
    className,
    sx = null,
}) {
    if (children == null || children === false) return null;
    return (
        <MuiMaterials.Box
            className={className ? `${className} hide-scrollbar` : "hide-scrollbar"}
            sx={{
                flexShrink: 0,
                display: "flex",
                flexWrap: "nowrap",
                alignItems: grid ? "center" : "stretch",
                gap: 1,
                px: { xs: 1, sm: 2 },
                py: 0.75,
                borderBottom: `1px solid ${border}`,
                backgroundColor: paper,
                minWidth: 0,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                ...ACTION_BAR_SCROLL_SX,
                ...ACTION_SCROLL_CHILD_SX,
                ...(sx || {}),
            }}
        >
            {children}
        </MuiMaterials.Box>
    );
}

const INLINE_HYSTERESIS_PX = 8;

/**
 * Shared WindowChrome / ModalChrome header: [site | tool] ........ [actions][X]
 * When that row overflows, actions move to a strip below. Does not mutate child DOM.
 * Layout and Cable Health keep their own ChromeActionBar and do not pass `actions` here.
 */
export default function ChromeHeaderBar({
    leading,
    tool = null,
    afterTool = null,
    actions = null,
    onClose,
    closeAriaLabel = "Close",
    paper,
    border,
    textSecondary = null,
}) {
    const hasLeading = leading != null && leading !== false;
    const hasTool = tool != null && tool !== false;
    const hasAfterTool = afterTool != null && afterTool !== false;
    const hasActions = actions != null && actions !== false;
    const dividerColor =
        textSecondary
        || getWindowChromeThemeColor("#666666", "#c4a574");

    const narrowChrome = useNarrowChrome();
    const rootRef = useRef(null);
    const [placeInBar, setPlaceInBar] = useState(false);
    const placeInBarRef = useRef(false);
    const neededWidthRef = useRef(0);
    placeInBarRef.current = placeInBar;

    useLayoutEffect(() => {
        if (!hasActions) {
            setPlaceInBar(false);
            return undefined;
        }
        const root = rootRef.current;
        if (!root) return undefined;

        let rafId = 0;
        const applyPlaceInBar = (next) => {
            if (placeInBarRef.current === next) return;
            placeInBarRef.current = next;
            setPlaceInBar(next);
        };

        const measure = () => {
            if (placeInBarRef.current) {
                if (
                    neededWidthRef.current > 0
                    && root.clientWidth >= neededWidthRef.current + INLINE_HYSTERESIS_PX
                ) {
                    applyPlaceInBar(false);
                }
                return;
            }
            const styles = window.getComputedStyle(root);
            const pad = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
            const needed = pad + Array.from(root.children).reduce((sum, el) => sum + el.offsetWidth, 0);
            neededWidthRef.current = needed;
            if (needed > root.clientWidth + 1) {
                applyPlaceInBar(true);
            }
        };

        // Defer observer work so Chrome does not report
        // "ResizeObserver loop completed with undelivered notifications"
        // (CRA treats that as a full-screen error overlay).
        const scheduleMeasure = () => {
            if (rafId) return;
            rafId = window.requestAnimationFrame(() => {
                rafId = 0;
                measure();
            });
        };

        measure();
        const ro = new ResizeObserver(scheduleMeasure);
        ro.observe(root);
        // Child size changes (buttons appearing, labels growing) do not resize the
        // full-width header, so the row must be measured from those children too.
        Array.from(root.children).forEach((el) => ro.observe(el));
        window.addEventListener("resize", scheduleMeasure);
        return () => {
            if (rafId) window.cancelAnimationFrame(rafId);
            ro.disconnect();
            window.removeEventListener("resize", scheduleMeasure);
        };
    }, [hasActions, hasLeading, hasTool, hasAfterTool, placeInBar]);

    const renderCloseButton = (key) => (
        <MuiMaterials.IconButton
            key={key}
            onClick={onClose}
            aria-label={closeAriaLabel}
            sx={{
                flexShrink: 0,
                mr: "-6px",
                color: getWindowChromeThemeColor("#333333", BRAND_GOLD),
                "& .MuiSvgIcon-root": {
                    color: "inherit",
                },
            }}
        >
            <CloseIcon />
        </MuiMaterials.IconButton>
    );

    return (
        <ChromeHeaderLayoutContext.Provider value={{ placeInBar }}>
        <MuiMaterials.Box
            ref={rootRef}
            sx={{
                display: "flex",
                flexWrap: placeInBar ? "wrap" : "nowrap",
                alignItems: "center",
                minWidth: 0,
                width: "100%",
                px: placeInBar ? 0 : { xs: 1, sm: 2 },
                pr: placeInBar ? { xs: 0.5, sm: 1 } : { xs: 0.5, sm: 1 },
                pt: placeInBar ? { xs: 1, sm: 1.5 } : { xs: 1, sm: 1.5 },
                pb: placeInBar ? 0 : { xs: 1, sm: 1.5 },
                borderBottom: `1px solid ${border}`,
                backgroundColor: paper,
                flexShrink: 0,
                boxSizing: "border-box",
            }}
        >
            <MuiMaterials.Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    overflow: "hidden",
                    flex: placeInBar ? "1 1 auto" : "0 0 auto",
                    minWidth: placeInBar ? 0 : undefined,
                    maxWidth: placeInBar ? "calc(100% - 52px)" : "100%",
                    order: 1,
                    px: placeInBar ? { xs: 1, sm: 2 } : 0,
                    pb: placeInBar ? { xs: 1, sm: 1.5 } : 0,
                }}
            >
                {hasLeading ? leading : null}
                {hasLeading && hasTool ? (
                    <MuiMaterials.Box
                        aria-hidden
                        sx={{
                            alignSelf: "center",
                            width: 1.5,
                            height: "1.4rem",
                            flexShrink: 0,
                            borderRadius: 0.5,
                            backgroundColor: dividerColor,
                            opacity: 0.75,
                        }}
                    />
                ) : null}
                {hasTool ? tool : null}
                {hasAfterTool ? (
                    <MuiMaterials.Box
                        aria-hidden
                        sx={{
                            alignSelf: "center",
                            width: 1.5,
                            height: "1.4rem",
                            flexShrink: 0,
                            borderRadius: 0.5,
                            backgroundColor: dividerColor,
                            opacity: 0.75,
                            mx: 0.5,
                        }}
                    />
                ) : null}
                {hasAfterTool ? afterTool : null}
            </MuiMaterials.Box>

            {hasActions ? (
                <MuiMaterials.Box
                    sx={
                        placeInBar
                            ? {
                                display: "flex",
                                flexWrap: "nowrap",
                                alignItems: "center",
                                gap: 1,
                                flex: "1 0 100%",
                                order: 3,
                                minWidth: 0,
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                pt: "16px",
                                pb: "16px",
                                pl: { xs: "8px", sm: "12px" },
                                pr: "8px",
                                borderTop: `1px solid ${border}`,
                                ...(narrowChrome
                                    ? {
                                        flexWrap: "nowrap",
                                        overflowX: "hidden",
                                        overflowY: "visible",
                                        alignItems: "stretch",
                                    }
                                    : ACTION_BAR_SCROLL_SX),
                                ...ACTION_SCROLL_CHILD_SX,
                            }
                            : {
                                display: "flex",
                                flexWrap: "nowrap",
                                alignItems: "center",
                                gap: 1,
                                flex: "0 0 auto",
                                width: "auto",
                                order: 2,
                                ml: "auto",
                                minWidth: 0,
                                "& > *": {
                                    flex: "0 0 auto !important",
                                    width: "auto !important",
                                    maxWidth: "none !important",
                                    minWidth: "auto !important",
                                },
                                "& .MuiFormControl-root": {
                                    minWidth: 0,
                                    maxWidth: "none",
                                },
                            }
                    }
                >
                    {actions}
                    {placeInBar ? null : renderCloseButton("chrome-close-inline")}
                </MuiMaterials.Box>
            ) : null}

            {placeInBar || !hasActions ? (
                <MuiMaterials.Box
                    sx={{
                        flexShrink: 0,
                        order: 2,
                        ml: "auto",
                    }}
                >
                    {renderCloseButton("chrome-close-end")}
                </MuiMaterials.Box>
            ) : null}
        </MuiMaterials.Box>
        </ChromeHeaderLayoutContext.Provider>
    );
}
